import React from 'react'
import { IconButton, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

type StepperProps = {
  label: string
  value: number
  min: number
  max: number
  unit?: string
  onChange: (value: number) => void
  rootSx?: SxProps<Theme>
  rowSx?: SxProps<Theme>
  labelSx?: SxProps<Theme>
}

const NumberStepper: React.FC<StepperProps> = ({
  label,
  value,
  min,
  max,
  unit,
  onChange,
  rootSx,
  rowSx,
  labelSx,
}) => (
  <Stack spacing={0.5} sx={rootSx}>
    <Typography
      variant="caption2"
      sx={[
        {
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'text.secondary',
          fontSize: 11,
        },
        ...(Array.isArray(labelSx) ? labelSx : [labelSx]),
      ]}
    >
      {label}
    </Typography>
    <Stack
      direction="row"
      sx={[
        { alignItems: 'center' },
        {
          border: '1px solid',
          borderColor: 'neutral.gray',
          borderRadius: 1,
          height: 36,
        },
        ...(Array.isArray(rowSx) ? rowSx : [rowSx]),
      ]}
    >
      <IconButton
        type="button"
        size="small"
        aria-label={`Decrease ${label.toLowerCase()}`}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        sx={{ padding: '4px' }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Typography
        variant="body2"
        sx={{ minWidth: 30, textAlign: 'center', fontWeight: 600 }}
      >
        {value}
        {unit ? <span style={{ opacity: 0.6 }}>{unit}</span> : null}
      </Typography>
      <IconButton
        type="button"
        size="small"
        aria-label={`Increase ${label.toLowerCase()}`}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
        sx={{ padding: '4px' }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  </Stack>
)

type Props = {
  quantity: number
  hours: number
  onQuantityChange: (quantity: number) => void
  onHoursChange: (hours: number) => void
  minQuantity?: number
  maxQuantity?: number
  minHours?: number
  maxHours?: number
}

export const QuantityHoursControls: React.FC<Props> = ({
  quantity,
  hours,
  onQuantityChange,
  onHoursChange,
  minQuantity = 1,
  maxQuantity = 50,
  minHours = 4,
  maxHours = 10,
}) => (
  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
    <NumberStepper
      label="Staff"
      value={quantity}
      min={minQuantity}
      max={maxQuantity}
      onChange={onQuantityChange}
    />
    <NumberStepper
      label="Hours"
      value={hours}
      min={minHours}
      max={maxHours}
      onChange={onHoursChange}
    />
  </Stack>
)
